import { useState } from "react";
import { Badge, Button, Card } from "@insights/ui";
import { Download, Eye } from "lucide-react";
import { useParams } from "react-router";
import { Loading, PageHeader } from "../../../shared/components";
import { AppShell } from "../../../shared/layout";
import { useBilling } from "../hooks/useBilling";

function formatInvoiceAmount(amount: number, currency: string) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);
}

export function InvoicesPage() {
  const { workspaceId } = useParams();
  const billing = useBilling(workspaceId, {
    includeInvoices: true,
    includeProducts: false,
  });
  const [portalError, setPortalError] = useState("");
  const [portalOpening, setPortalOpening] = useState(false);
  const canViewInvoices = billing.query.data?.role === "owner";

  async function openBillingPortal() {
    setPortalError("");
    setPortalOpening(true);

    try {
      await billing.openPortal();
    } catch (caught) {
      setPortalError(
        caught instanceof Error
          ? caught.message
          : "Unable to open the billing portal",
      );
    } finally {
      setPortalOpening(false);
    }
  }

  return (
    <AppShell>
      <PageHeader eyebrow="Billing history" title="Invoices" />
      {billing.query.isLoading && <Loading />}
      {billing.query.error && (
        <Card className="billing-invoices">
          <p className="error">Unable to load billing information.</p>
        </Card>
      )}
      {!billing.query.isLoading &&
        !billing.query.error &&
        !canViewInvoices && (
          <Card className="billing-invoices">
            <h2>Invoice access</h2>
            <p className="muted">
              Only workspace owners can view invoices.
            </p>
          </Card>
        )}
      {canViewInvoices && (
        <Card
          as="section"
          className="billing-invoices"
          aria-labelledby="billing-invoices-title"
        >
          <div className="billing-invoices-header">
            <h2 id="billing-invoices-title">Invoice history</h2>
            <div className="billing-invoices-header-actions">
              {billing.invoices.isFetching && <span>Refreshing…</span>}
              {billing.query.data?.stripe_customer_id && (
                <Button
                  color="neutral"
                  size="small"
                  onClick={() => void openBillingPortal()}
                  disabled={portalOpening}
                >
                  {portalOpening ? "Opening…" : "Open billing portal"}
                </Button>
              )}
            </div>
          </div>
          {portalError && <p className="error">{portalError}</p>}
          {billing.invoices.error && (
            <p className="error">Unable to load invoices.</p>
          )}
          <div className="billing-invoice-list">
            {billing.invoices.data?.items.map((invoice) => (
              <article className="billing-invoice-row" key={invoice.id}>
                <div className="billing-invoice-identity">
                  <strong>{invoice.number ?? "Invoice"}</strong>
                  <span>
                    {new Date(invoice.created * 1000).toLocaleDateString()}
                  </span>
                </div>
                <Badge
                  className={
                    invoice.status === "paid"
                      ? "success"
                      : invoice.status === "open"
                        ? "pending"
                        : ""
                  }
                >
                  {invoice.status ?? "unknown"}
                </Badge>
                <span className="billing-invoice-amount">
                  {formatInvoiceAmount(invoice.amountPaid, invoice.currency)}
                </span>
                <div className="billing-invoice-actions">
                  {invoice.hostedInvoiceUrl && (
                    <a
                      href={invoice.hostedInvoiceUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Eye />
                      View
                    </a>
                  )}
                  {invoice.invoicePdfUrl && (
                    <a
                      href={invoice.invoicePdfUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Download />
                      PDF
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
          {!billing.invoices.isLoading &&
            !billing.invoices.data?.items.length &&
            !billing.invoices.error && (
              <p className="muted">No invoices yet.</p>
            )}
        </Card>
      )}
    </AppShell>
  );
}
